<template>
  <MenuComponent
    :custom_title="$t('Menu.AutomationEvents')"
    :filtred_count="pageParams.totalCount">
    <template #filter>
      <sPopover
        v-model="visibleFilters"
        :border="false"
        :disabled="loading"
        trigger="manual"
        @clickoutside="visibleFilters = false">
        <sBadge
          slot="reference"
          :color="needDot ? 'red' : 'transparent'"
          :content="needDot"
          :dot="true"
          :offset-x="15"
          overlap>
          <sButton
            class="my-auto"
            color="var(--black-color)"
            data-qa="btn-open-filter"
            :disabled=" loading"
            icon="filter"
            type="text"
            @click="visibleFilters = !visibleFilters">
            {{ $t('Planning.Filter') }}
          </sButton>
        </sBadge>
        <div
          class="block-filters">
          <div class="block-filters__item">
            <div class="label-filter">
              {{ $t('Common.Date') }}
            </div>
            <DateRangeDialogWithType
              ref="dateRange"
              data-qa="filter-bar-date-range"
              :search.sync="filterDate"
              @apply="applyFilterDate"/>
            <div class="my-auto">
              <sButton
                :disabled="!filterDate || !filterDate[0] && !filterDate[1]"
                icon="cancel"
                small
                type="text"
                @click="clearFilter('date')"/>
            </div>
          </div>
          <div class="block-filters__item">
            <div class="label-filter">
              {{ $t('Common.Status') }}
            </div>
            <sSelect
              data-qa="filter-bar-status"
              :disabled="loading"
              :placeholder="$t('Common.Select')"
              :value.sync="filter.status"
              @update:value="applyFilterDate">
              <sOption
                class="px-8"
                :disabled="loading"
                :label="$t('AutomationEventsStatus.OK')"
                value="OK"/>
              <sOption
                class="px-8"
                :disabled="loading"
                :label="$t('AutomationEventsStatus.FAILED')"
                value="FAILED"/>
            </sSelect>
            <div class="my-auto">
              <sButton
                :disabled="!filter.status"
                icon="cancel"
                small
                type="text"
                @click="clearFilter('status')"/>
            </div>
          </div>
          <div class="block-filters__item">
            <div class="label-filter">
              {{ $t('Menu.Automation') }}
            </div>
            <sInput
              :disabled="loading"
              :placeholder="$t('Common.Select')"
              :value.sync="filter.automationId"
              @keydown_enter="applyFilterDate"/>
            <div class="my-auto">
              <sButton
                :disabled="!filter.automationId"
                icon="cancel"
                small
                type="text"
                @click="clearFilter('automationId')"/>
            </div>
          </div>
          <div class="block-filters__item">
            <div class="label-filter">
              {{ $t('Tenders.Tender') }}
            </div>
            <sInput
              :disabled="loading"
              :placeholder="$t('Common.Select')"
              :value.sync="filter.tenderId"
              @keydown_enter="applyFilterDate"/>
            <div class="my-auto">
              <sButton
                :disabled="!filter.tenderId"
                icon="cancel"
                small
                type="text"
                @click="clearFilter('tenderId')"/>
            </div>
          </div>
        </div>
      </sPopover>
    </template>
    <template #items>
      <EmptyBlock
        v-if="!loading && eventList.length == 0"
        class="mt-4"
        style="max-width: 1024px; width: 100%; margin: 0 auto;"
        :text="$t('Tenders.NoData')"/>
      <v-data-table
        v-else-if="!loading || eventList.length"
        id="automationEventsList"
        calculate-widths
        class="mt-4"
        disable-pagination
        :expanded="expanded"
        fixed-header
        :headers="headers"
        hide-default-footer
        item-key="id"
        :items="eventList"
        :loading-text="$t('Tenders.Loading')"
        :no-data-text="$t('Tenders.NoData')"
        :no-results-text="$t('Tenders.NoData')"
        style="max-width: 1024px; width: 100%; margin: 0 auto;">
        <template v-slot:item="{ item: itemObject, isExpanded }">
          <tr
            :class="{'pointer': true, 'v-data-table__expanded v-data-table__expanded__row': isExpanded}"
            @click="expandRow(itemObject)">
            <td class="text-left">
              <router-link
                target="_blank"
                :to="{name: 'editAutomation', params: {org: itemObject.organizationItId, id: itemObject.automationId} }">
                {{ itemObject.title }}
              </router-link>
            </td>
            <td class="text-left">
              <router-link
                target="_blank"
                :to="{name: 'tenderitem', params: {org: itemObject.organizationItId, id: itemObject.tenderId} }">
                {{ itemObject.tenderTitle }}
              </router-link>
            </td>
            <td class="text-left">
              {{ itemObject.organizationTitle }}
            </td>
            <td class="text-left">
              <sTooltip
                :border="false"
                :disabled="!itemObject.errorObject"
                placement="top"
                popper-class="py-0 px-0"
                trigger="hover"
                width="200">
                <StatusChip
                  class="font-size-12 px-2 my-auto"
                  :name="$t(`AutomationEventsStatus.${itemObject.status}`)"
                  :status="itemObject.status"/>
                <sAlert
                  v-if="itemObject.errorObject"
                  slot="content"
                  type="error">
                  {{ itemObject.errorObject.ClassName }}
                  <template slot="desc">
                    <div>
                      {{ itemObject.errorObject.Message }}
                    </div>
                    <div>
                      {{ itemObject.errorObject.StackTrace }}
                    </div>
                  </template>
                </sAlert>
              </sTooltip>
            </td>
            <td>
              {{ $d(new Date(itemObject.datetime), "shortDate") }} {{ $d(new Date(itemObject.datetime), "time") }}
            </td>
          </tr>
        </template>
        <template #expanded-item="{ headers: expandedHeaders, item: groupExpand }">
          <tr
            class="v-data-table__expanded width-100">
            <td
              class="expanded-table-td pa-0 text-left"
              :colspan="expandedHeaders.length">
              <div class="flex flex-wrap gap-10">
                <div class="flex flex-wrap flex-between gap-4 p-4">
                  <KeyValueWithCopy
                    caption="Id"
                    :value="groupExpand.id"/>
                  <KeyValueWithCopy
                    :caption="$t('Menu.Automation')"
                    class="pointer"
                    :value="groupExpand.automationId"
                    @click="setFilter('automationId', groupExpand.automationId)"/>
                  <KeyValueWithCopy
                    :caption="$t('Tenders.Tender')"
                    class="pointer"
                    :value="groupExpand.tenderId"
                    @click="setFilter('tenderId', groupExpand.tenderId)"/>
                  <KeyValueWithCopy
                    :caption="$t('Common.Organization')"
                    :value="groupExpand.organizationItId"/>
                </div>
                <JsonViewer
                  v-if="groupExpand.error"
                  class="mt-2 text-left grid py-2 px-2"
                  style="background-color: var(--alert-error-background);"
                  :value="groupExpand.error"/>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </template>
    <template #loader>
      <InfinityUpdater
        ref="infinity_updater"
        :action="getMore"
        :active="needMore"
        call-on-mount
        class="mt-3"
        :loading="loading"/>
    </template>
  </MenuComponent>
</template>
<script>

import { debounce } from 'toolkit/utils/util'
import MenuComponent from 'pages/templates/MenuComponent.vue'
import JsonViewer from 'components/common/elements/JsonViewer.vue'
import StatusChip from 'components/common/elements/StatusChip.vue'
import EmptyBlock from 'components/common/elements/EmptyBlock.vue'
import InfinityUpdater from 'components/extends/InfinityUpdater.vue'
import KeyValueWithCopy from 'components/common/elements/KeyValueWithCopy.vue'
import DateRangeDialogWithType from 'components/common/dialogs/DateRangeDialogWithType.vue'


import AutomationsApi from '@/api/automationsApi'
const automations_api = new AutomationsApi()

function parseError(error) {
  if (!error) { return null }
  try {
    return JSON.parse(error)
  } catch (e) {
    return null
  }
}

export default {
  name: 'automationEvents',
  data() {
    return {
      loading: true,
      visibleFilters: false,
      pageParams: {
        totalCount: 0,
        count: 25,
        page: 1,
      },
      filter: {
        dateFrom: null,
        dateTo: null,
        status: null,
        tenderId: null,
        automationId: null,
      },
      oldFilter: null,
      needMore: true,
      eventList: [],
      headers: [
        {text: this.$t('Menu.Automation'), value: 'automation', width: '250px', disableFilter: true, sortable: false, order: 1},
        {text: this.$t('Tenders.Tender'), value: 'tender', width: '250px', sortable: false, order: 2},
        {text: this.$t('Common.Organization'), value: 'org', sortable: false, order: 3},
        {text: this.$t('Common.Status'), value: 'status', width: '90px', sortable: false, order: 4},
        {text: this.$t('Common.Date'), value: 'date', width: '135px', sortable: false, order: 5},
      ],
      expanded: []
    }
  },
  methods: {
    applyFilterDate() {
      this.pageParams.page = 1
      this.applyDate()
    },
    setFilter(key, value) {
      this.$set(this.filter, key, value)
      this.applyFilterDate()
    },
    expandRow(item) {
      let ind = this.expanded.findIndex(el => el.id == item.id)

      if (ind === -1) {
        this.expanded.push(item)
      } else {
        this.expanded.splice(ind, 1)
      }
    },
    clearFilter(key) {
      if (key === 'date') {
        this.filterDate = null
        if (this.$refs.dateRange) {
          this.$refs.dateRange.dateFrom = null
          this.$refs.dateRange.dateTo = null
        }
        this.applyFilterDate()
      } else if (key === 'status') {
        this.filter.status = null
        this.applyFilterDate()
      } else if (key === 'automationId' || key === 'tenderId') {
        this.filter[key] = null
        this.applyFilterDate()
      }
    },
    applyDate() {
      if (JSON.stringify(this.oldFilter) !== JSON.stringify(this.filter)) {
        this.eventList = []
        this.needMore = true
        this.getLogs()
      }
    },
    getMore: debounce(function () {
      if (!this.needMore) {
        return
      }

      this.pageParams.page += 1
      this.getLogs()
    }, 250),
    getLogs: debounce(function () {
      if (!this.needMore) {
        return
      }
      this.visibleFilters = false
      this.loading = true
      const data = {
        count: this.pageParams.count,
        page: this.pageParams.page,
        dateFrom: this.filter.dateFrom,
        dateTo: this.filter.dateTo,
        status: this.filter.status,
        tenderId: this.filter.tenderId,
        automationId: this.filter.automationId,
      }

      automations_api.api_getAutomationLogs(data).then(result => {
        if (result) {
          this.pageParams.totalCount = result.totalCount
          this.eventList = []
            .concat(this.eventList)
            .concat(result.items?.map(i => ({
              ...i,
              errorObject: parseError(i.error)
            })) || [])
            
          this.needMore = this.pageParams.totalCount > this.eventList?.length
        } else {
          this.needMore = false
        }
      }).finally(() => {
        this.loading = false
        this.oldFilter = this.$_.cloneDeep(this.filter)
      })
    }, 250),
  },
  computed: {
    needDot() {
      return Object.keys(this.filter).some(key => this.filter[key])
    },
    filterDate: {
      get() {
        const result = [
          this.filter.dateFrom || null,
          this.filter.dateTo || null
        ]
        return result
      },
      set(newValue) {
        if (!newValue) {
          this.$set(this.filter, 'dateFrom', null)
          this.$set(this.filter, 'dateTo', null)
        } else {
          this.$set(this.filter, 'dateFrom', newValue[0])
          this.$set(this.filter, 'dateTo', newValue[1])
        }
      }
    },
  },
  components: {
    EmptyBlock,
    StatusChip,
    JsonViewer,
    MenuComponent,
    InfinityUpdater,
    KeyValueWithCopy,
    DateRangeDialogWithType
  },
  created() {
    if (this.$route?.query?.automationId) {
      this.filter.automationId = this.$route.query.automationId
      this.$router.replace('')
    }
    if (this.$route?.query?.tenderId) {
      this.filter.tenderId = this.$route.query.tenderId
      this.$router.replace('')
    }
    this.getLogs()
  },
  props: {},
}
</script>
<style lang="scss">
  #automationEventsList {
    & tr.v-data-table__expanded.v-data-table__expanded__row {
      & td,
      & th {
        border-bottom: thin solid transparent !important;
      }
    }
  }
</style>
<style scoped lang="scss">
.block-filters {
  display: grid;
  border-radius: 4px;
  border: 2px solid var(--white-color);
  background: var(--v-main__light_green-base);
  padding: 24px;
  gap: 10px;
  align-items: center;
  grid-template-columns: auto;
  &__item {
    display: grid;
    gap: 5px;
    grid-template-columns: 110px 1fr 16px;
    align-items: center;
    text-align: left;
  }
}
</style>
